<script lang="ts">
export default {
  name: 'PaymentMethodPreview',
}
</script>
<script setup lang="ts" >
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { KkIcon } from '../../../../components/base'
import { translatePaymentMethods } from '../../../../modules/paymentMethod'
import type { ServiceType, MethodInfo, MethodInfoAvailableOnly, PaymentMethodPreview } from 'src/biz_spa/domains/paymentMethod/types'
const { t } = useI18n()

interface PreviewTableData {
  label: string
  available: boolean
  displayDetail: boolean
  availableValues: string[]
  unavailableValues: string[]
}

interface Props {
  serviceType: ServiceType
  paymentMethodPreview: PaymentMethodPreview
}

const props = defineProps<Props>()

const tableData = computed<PreviewTableData[]>(() => {
  const result: PreviewTableData[] = []

  switch(props.serviceType) {
  case('tabelog'):
    if (!props.paymentMethodPreview.tabelog) return []

    if (props.paymentMethodPreview.tabelog.creditCard != null) result.push(createTableRowDataFromMethodInfo('creditCard', props.paymentMethodPreview.tabelog.creditCard))
    if (props.paymentMethodPreview.tabelog.electronicMoney != null) result.push(createTableRowDataFromMethodInfo('electronicMoney', props.paymentMethodPreview.tabelog.electronicMoney))
    if (props.paymentMethodPreview.tabelog.qrCodePayment != null) result.push(createTableRowDataFromMethodInfo('qrCodePayment', props.paymentMethodPreview.tabelog.qrCodePayment))

    break
  case('google'):
    if (!props.paymentMethodPreview.google) return []

    if (props.paymentMethodPreview.google.creditCard != null) result.push(createTableRowDataFromMethodInfo('creditCard', props.paymentMethodPreview.google.creditCard))
    if (props.paymentMethodPreview.google.debitCard != null) result.push(createTableRowDataFromMethodInfoAvailableOnly('debitCard', props.paymentMethodPreview.google.debitCard))
    if (props.paymentMethodPreview.google.cashOnly != null) result.push(createTableRowDataFromMethodInfoAvailableOnly('cashOnly', props.paymentMethodPreview.google.cashOnly))
    if (props.paymentMethodPreview.google.check != null) result.push(createTableRowDataFromMethodInfoAvailableOnly('check', props.paymentMethodPreview.google.check))
    if (props.paymentMethodPreview.google.nfcMobile != null) result.push(createTableRowDataFromMethodInfoAvailableOnly('nfcMobile', props.paymentMethodPreview.google.nfcMobile))

    break
  case('gnavi'):
    if (!props.paymentMethodPreview.gnavi) return []

    if (props.paymentMethodPreview.gnavi.creditCard != null) result.push(createTableRowDataFromMethodInfo('creditCard', props.paymentMethodPreview.gnavi.creditCard))
    if (props.paymentMethodPreview.gnavi.icCardAndOthers != null) result.push(createTableRowDataFromMethodInfo('icCardAndOthers', props.paymentMethodPreview.gnavi.icCardAndOthers))

    break
  case('retty'):
    if (!props.paymentMethodPreview.retty) return []

    if (props.paymentMethodPreview.retty.creditCard != null) result.push(createTableRowDataFromMethodInfo('creditCard', props.paymentMethodPreview.retty.creditCard))
    if (props.paymentMethodPreview.retty.icCard != null) result.push(createTableRowDataFromMethodInfo('icCard', props.paymentMethodPreview.retty.icCard))

    break
  case('hotPepperGourmet'):
    if (!props.paymentMethodPreview.hotPepperGourmet) return []

    if (props.paymentMethodPreview.hotPepperGourmet.creditCard != null) result.push(createTableRowDataFromMethodInfo('creditCard', props.paymentMethodPreview.hotPepperGourmet.creditCard))
    if (props.paymentMethodPreview.hotPepperGourmet.electronicMoney != null) result.push(createTableRowDataFromMethodInfo('electronicMoney', props.paymentMethodPreview.hotPepperGourmet.electronicMoney))
    if (props.paymentMethodPreview.hotPepperGourmet.qrCodePayment != null) result.push(createTableRowDataFromMethodInfo('qrCodePayment', props.paymentMethodPreview.hotPepperGourmet.qrCodePayment))

    break
  case('tripAdvisor'):
    if (!props.paymentMethodPreview.tripAdvisor) return []

    if (props.paymentMethodPreview.tripAdvisor.creditCard != null) result.push(createTableRowDataFromMethodInfo('creditCard', props.paymentMethodPreview.tripAdvisor.creditCard))
    if (props.paymentMethodPreview.tripAdvisor.digitalPayment != null) result.push(createTableRowDataFromMethodInfoAvailableOnly('digitalPayment', props.paymentMethodPreview.tripAdvisor.digitalPayment))
    if (props.paymentMethodPreview.tripAdvisor.cashOnly != null) result.push(createTableRowDataFromMethodInfoAvailableOnly('cashOnly', props.paymentMethodPreview.tripAdvisor.cashOnly))
    break
  case 'yahooPlace':
    if (!props.paymentMethodPreview.yahooPlace) return []

    if (props.paymentMethodPreview.yahooPlace.creditCard != null) result.push(createTableRowDataFromMethodInfo('creditCard', props.paymentMethodPreview.yahooPlace.creditCard))
    if (props.paymentMethodPreview.yahooPlace.electronicMoney != null) result.push(createTableRowDataFromMethodInfo('electronicMoney', props.paymentMethodPreview.yahooPlace.electronicMoney))
    if (props.paymentMethodPreview.yahooPlace.transportationElectronicMoney != null) result.push(createTableRowDataFromMethodInfo('transportationElectronicMoney', props.paymentMethodPreview.yahooPlace.transportationElectronicMoney))
    if (props.paymentMethodPreview.yahooPlace.qrCodePayment != null) result.push(createTableRowDataFromMethodInfo('qrCodePayment', props.paymentMethodPreview.yahooPlace.qrCodePayment))
    if (props.paymentMethodPreview.yahooPlace.cashOnly != null) result.push(createTableRowDataFromMethodInfoAvailableOnly('cashOnly', props.paymentMethodPreview.yahooPlace.cashOnly))
    break
  }

  return result
})

const errors = computed<string[]>(() => {
  return props.paymentMethodPreview[props.serviceType]?.errors || []
})

const createTableRowDataFromMethodInfo = (categoryKey: string, methodInfo: MethodInfo): PreviewTableData => {
  return {
    label: t(`models.storePaymentMethod.${props.serviceType}.${categoryKey}`),
    available: methodInfo.available,
    displayDetail: true,
    availableValues: translatePaymentMethods(methodInfo.availableValues),
    unavailableValues: translatePaymentMethods(methodInfo.unavailableValues),
  }
}

const createTableRowDataFromMethodInfoAvailableOnly = (categoryKey: string, methodInfo: MethodInfoAvailableOnly): PreviewTableData => {
  return {
    label: t(`models.storePaymentMethod.${props.serviceType}.${categoryKey}`),
    available: methodInfo.available,
    displayDetail: false,
    availableValues: [],
    unavailableValues: [],
  }
}

const displayDetail = (previewTableData: PreviewTableData): boolean => {
  // 詳細表示が不要なカテゴリの場合
  if (!previewTableData.displayDetail) return false
  // カテゴリ自体が利用不可の時
  if (!previewTableData.available) return false
  // カテゴリは有効だが、全て未設定のためavailableValuesがない時
  if (previewTableData.available && previewTableData.availableValues.length === 0) return false

  return true
}
</script>

<template>
  <div>
    <div class="p-table">
      <div class="p-table__content">
        <table class="paymentMethod">
          <thead>
            <tr>
              <th class="paymentMethod__paymentTypeSection">{{ t('pages.CompaniesLocationStorePaymentMethodsEdit.preview.paymentMethod') }}</th>
              <th>{{ t('pages.CompaniesLocationStorePaymentMethodsEdit.preview.detail') }}</th>
            </tr>
            <tr class="p-table__content__space">
              <td colspan="4" />
            </tr>
          </thead>
          <tbody>
            <tr v-for="(data, index) in tableData" :key="index">
              <td class="paymentMethod__section">
                {{ data.label }}
                <KkIcon class="paymentMethod__icon" :name="data.available ? 'iconOk' : 'iconNg'" />
              </td>
              <td>
                <div v-if="displayDetail(data)">
                  <div class="detail">
                    <p class="detail__label">{{ t('pages.CompaniesLocationStorePaymentMethodsEdit.preview.available') }}</p>
                    {{ data.availableValues.join(' / ') }}
                  </div>
                  <div class="detail">
                    <p class="detail__label">{{ t('pages.CompaniesLocationStorePaymentMethodsEdit.preview.unavailable') }}</p>
                    {{ data.unavailableValues.join(' / ') }}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="error">
      <p v-for="(error, index) in errors" :key="index">{{ error }}</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';
.p-table {
    margin-top: 0;
  }
.paymentMethod {
  height: 100%;
  &__paymentTypeSection {
    width: 25%;
  }
  &__section {
    display: flex;
    align-items: center;
    background-color: #f8f9fa;
    height: 100%;
  }
  &__icon {
    width: 12px;
    margin-left: 5px;
  }
  td:empty {
    display: table-cell;
  }
}

.detail {
  &:first-child {
    margin-bottom: 16px;
  }

  &__label {
    font-weight: bold;
  }
}

.error {
  margin-top: 16px;
  color: $color-accent-primary;
}
</style>
